/**
 * Form Field Renderer Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 *
 * Renders form fields based on schema definitions
 */
import React, { useMemo } from "react";

import { Box, MenuItem, TextField, Typography } from "@mui/material";

import { FieldSchema, FormState } from "../types";
import { OneofValue, groupFieldsByOneof } from "../utils/form-utils";
import { InputFactory } from "./input-factory";

interface FormFieldRendererProps {
  fields: FieldSchema[];
  formState: FormState;
  onFieldChange: (fieldName: string, value: unknown) => void;
}

export const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  fields,
  formState,
  onFieldChange,
}) => {
  const { plainFields, oneofGroups } = useMemo(
    () => groupFieldsByOneof(fields),
    [fields],
  );
  const oneofGroupNames = useMemo(
    () => Object.keys(oneofGroups),
    [oneofGroups],
  );

  // Oneof members (e.g. a top-level request oneof) are mutually exclusive
  // alternatives, not independent fields — see MessageInput for why a flat
  // { quota: {...} } sibling key is silently dropped by the message
  // constructor instead of erroring.
  const handleOneofCaseChange = (groupName: string, newCase: string) => {
    onFieldChange(
      groupName,
      newCase ? { case: newCase, value: undefined } : undefined,
    );
  };

  const renderOneofGroup = (groupName: string) => {
    const members = oneofGroups[groupName];
    const oneofValue = formState.data[groupName] as OneofValue | undefined;
    const currentCase = oneofValue?.case ?? "";
    const selectedMember = members.find((m) => m.name === currentCase);

    return (
      <Box
        key={groupName}
        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
      >
        <TextField
          select
          size="small"
          fullWidth
          variant="outlined"
          label={`${groupName} (choose one)`}
          value={currentCase}
          onChange={(e) => handleOneofCaseChange(groupName, e.target.value)}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {members.map((member) => (
            <MenuItem key={member.name} value={member.name}>
              {member.name}
            </MenuItem>
          ))}
        </TextField>
        {selectedMember && (
          <InputFactory
            field={selectedMember}
            value={oneofValue?.value}
            onChange={(_fieldName, fieldValue) =>
              onFieldChange(groupName, { case: currentCase, value: fieldValue })
            }
            error={formState.errors[groupName]}
            touched={formState.touched[groupName]}
          />
        )}
      </Box>
    );
  };

  if (fields.length === 0) {
    return (
      <Box
        sx={{
          py: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" color="text.secondary">
            📝
          </Typography>
        </Box>
        <Typography variant="body1" color="text.primary" gutterBottom>
          No Fields Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This method doesn&apos;t require any input parameters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {plainFields.map((field) => {
        const fieldValue = formState.data[field.name];
        return (
          <Box key={field.name}>
            <InputFactory
              field={field}
              value={fieldValue}
              onChange={onFieldChange}
              error={formState.errors[field.name]}
              touched={formState.touched[field.name]}
            />
          </Box>
        );
      })}
      {oneofGroupNames.map((groupName) => renderOneofGroup(groupName))}
    </Box>
  );
};
